/**
 * KkdKurallariKarti.jsx — KKD (kişisel koruyucu donanım) için "bu fuarda hangi kalem istenir" (backlog #374, backend #373).
 *
 * Her aktif katalog kalemi için bir seviye: İstenmiyor (0) · Uyarı (1) · Engelliyor (2). Seviye, kişilerin KKD durumunu belirler
 * (Kişi Detayı › Kontroller'de işaretlenen maddelere göre): engelleyen madde eksikse KKD EKSİK (Kişi Listesi / firma listelerinde kırmızı
 * kare, kart kararı SARI "Alanda Kart Alabilir"), uyarı maddesi eksikse yalnızca UYARI (kare sarı, kart kararı değişmez), hiçbir
 * madde istenmiyorsa ISTENMIYOR (soluk gri kare). Satırı hiç yazılmamış fuar hiçbir şey istemez.
 *
 * İki yerde takılır: Fuar Kuralları › KKD (`fuarId` = seçili fuar) ve Global Kurallar › KKD (`fuarId` 0 = global ŞABLON). Şablon fuara
 * KOPYALANIR, miras değildir: fuar sekmesindeki "Global şablonu uygula" şablonu TASLAĞA yükler, kaydedince fuara yazılır.
 *
 * Kayıt OTORİTERDİR (gövdede olmayan aktif kalem 0 olur) — bu yüzden her zaman bütün kalemler gönderilir. Taslak + yapışkan kaydet çubuğu,
 * FuarTalepTurleriTab ile aynı düzen. Kalem kataloğunun kendisi (ekle / düzenle / pasifleştir) KkdKalemKatalogu'nda, Global Kurallar'da.
 *
 * @param {number} fuarId       0 = global şablon
 * @param {number} reloadToken  değişince yeniden okunur (katalog değiştiğinde Global Kurallar artırır)
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Alert, Badge, Button, ButtonGroup, Card, CardBody, CardHeader, Spinner, Table } from "reactstrap";
import { Globe, Shirt } from "lucide-react";
import { useTranslation } from "react-i18next";
import { getFuarKkdKurali, setFuarKkdKurali, KKD_SEVIYE, KKD_GLOBAL_FUAR_ID } from "../../services/kkdKuraliService";
import { KkdIkon } from "../Kisi/Detay/KkdKarti";

/** Seviye düğmeleri: sıra soldan sağa, seçiliyken rengi (Kişi Detayı'ndaki seviye rozetleriyle aynı: engelliyor kırmızı, uyarı sarı). */
const SEVIYELER = [
  { deger: KKD_SEVIYE.KAPALI, anahtar: "kapali", renk: "dark" },
  { deger: KKD_SEVIYE.UYARI, anahtar: "uyari", renk: "warning" },
  { deger: KKD_SEVIYE.ENGELLER, anahtar: "engelliyor", renk: "danger" },
];

const taslakOlustur = (kural) => Object.fromEntries((kural?.kalemler ?? []).map((k) => [k.kkdKalemiId, k.seviye ?? 0]));

export default function KkdKurallariKarti({ fuarId, reloadToken = 0 }) {
  const { t } = useTranslation("yonetim");
  const genel = Number(fuarId) === KKD_GLOBAL_FUAR_ID;
  const [kural, setKural] = useState(null);
  const [taslak, setTaslak] = useState({});
  const [okunuyor, setOkunuyor] = useState(true);
  const [kaydediliyor, setKaydediliyor] = useState(false);
  const [sablonYukleniyor, setSablonYukleniyor] = useState(false);
  const [hata, setHata] = useState(null);
  const [mesaj, setMesaj] = useState(null);

  // KURAL: fetch Effect içindeki async fonksiyonda (react-hooks/set-state-in-effect).
  useEffect(() => {
    let ignore = false;
    async function run() {
      setOkunuyor(true);
      setHata(null);
      try {
        const sonuc = await getFuarKkdKurali(fuarId);
        if (ignore) return;
        setKural(sonuc);
        setTaslak(taslakOlustur(sonuc));
      } catch (e) {
        if (!ignore) setHata(e.message);
      } finally {
        if (!ignore) setOkunuyor(false);
      }
    }
    run();
    return () => { ignore = true; };
  }, [fuarId, reloadToken]);

  useEffect(() => {
    if (!mesaj) return undefined;
    const zamanlayici = setTimeout(() => setMesaj(null), 4500);
    return () => clearTimeout(zamanlayici);
  }, [mesaj]);

  const kalemler = useMemo(() => kural?.kalemler ?? [], [kural]);
  const degisenSayisi = kalemler.filter((k) => (taslak[k.kkdKalemiId] ?? 0) !== (k.seviye ?? 0)).length;
  const istenenSayisi = kalemler.filter((k) => (taslak[k.kkdKalemiId] ?? 0) > KKD_SEVIYE.KAPALI).length;

  const seviyeSec = useCallback((kalemId, seviye) => setTaslak((onceki) => ({ ...onceki, [kalemId]: seviye })), []);

  async function kaydet() {
    setKaydediliyor(true);
    setHata(null);
    setMesaj(null);
    try {
      const sonuc = await setFuarKkdKurali(fuarId, Object.fromEntries(kalemler.map((k) => [k.kkdKalemiId, taslak[k.kkdKalemiId] ?? 0])));
      setKural(sonuc);
      setTaslak(taslakOlustur(sonuc));
      setMesaj(genel ? t("kkdKurallari.kaydedildiGenel") : t("kkdKurallari.kaydedildiFuar"));
    } catch (e) {
      setHata(e.message);
    } finally {
      setKaydediliyor(false);
    }
  }

  // Şablonu taslağa yükler (kopya): yazma "Kaydet"te. Şablonda olmayan / pasif kalem 0 olur.
  async function sablonuUygula() {
    setSablonYukleniyor(true);
    setHata(null);
    setMesaj(null);
    try {
      const sablon = await getFuarKkdKurali(KKD_GLOBAL_FUAR_ID);
      const sablonSeviyeleri = taslakOlustur(sablon);
      setTaslak(Object.fromEntries(kalemler.map((k) => [k.kkdKalemiId, sablonSeviyeleri[k.kkdKalemiId] ?? 0])));
      setMesaj(sablon.istenen ? t("kkdKurallari.sablonYuklendi") : t("kkdKurallari.sablonBos"));
    } catch (e) {
      setHata(e.message);
    } finally {
      setSablonYukleniyor(false);
    }
  }

  return (
    <>
      {hata && <Alert color="danger" toggle={() => setHata(null)} data-testid="kkd-kural-hata">{hata}</Alert>}
      {mesaj && <Alert color="success" data-testid="kkd-kural-mesaj">{mesaj}</Alert>}

      <Card data-testid="kkd-kurallari">
        <CardHeader className="d-flex align-items-center gap-2 flex-wrap">
          <Shirt size={16} className="text-primary" />
          <h6 className="card-title mb-0 flex-grow-1">
            {genel ? t("kkdKurallari.baslikGenel") : t("kkdKurallari.baslikFuar")}
            {kural && (
              <Badge color="light" className="text-dark ms-2" data-testid="kkd-kural-sayac">
                {t("kkdKurallari.istenenSayisi", { istenen: istenenSayisi, toplam: kalemler.length })}
              </Badge>
            )}
          </h6>
          {!genel && (
            <>
              <Link to="/Management/Projects/GlobalRules?tab=kkd" className="small text-muted text-decoration-underline">
                {t("kkdKurallari.kalemleriYonet")}
              </Link>
              <Button color="light" size="sm" className="border" disabled={okunuyor || sablonYukleniyor || kaydediliyor}
                      onClick={sablonuUygula} data-testid="kkd-sablon-uygula">
                {sablonYukleniyor ? <Spinner size="sm" className="me-1" /> : <Globe size={14} className="me-1" />}
                {t("kkdKurallari.globalSablonuUygula")}
              </Button>
            </>
          )}
        </CardHeader>
        <CardBody className="p-0">
          {okunuyor ? (
            <div className="text-center py-4"><Spinner color="primary" size="sm" /></div>
          ) : kalemler.length === 0 ? (
            <div className="text-muted text-center py-4" data-testid="kkd-kural-bos">{t("kkdKurallari.kalemYok")}</div>
          ) : (
            <div className="table-responsive">
              <Table className="align-middle mb-0">
                <thead className="table-light">
                  <tr>
                    <th className="ps-3">{t("kkdKurallari.kalem")}</th>
                    <th style={{ width: 330 }}>{t("kkdKurallari.seviye")}</th>
                  </tr>
                </thead>
                <tbody>
                  {kalemler.map((k) => {
                    const secili = taslak[k.kkdKalemiId] ?? 0;
                    return (
                      <tr key={k.kkdKalemiId} data-testid="kkd-kural-satir">
                        <td className="ps-3">
                          <span className={`d-inline-flex align-items-center gap-2 ${secili === KKD_SEVIYE.KAPALI ? "text-muted" : "fw-semibold"}`}>
                            <KkdIkon ad={k.ikon} size={18} className="flex-shrink-0" />
                            {k.ad}
                          </span>
                        </td>
                        <td>
                          <ButtonGroup size="sm">
                            {SEVIYELER.map((s) => (
                              <Button
                                key={s.deger} type="button" disabled={kaydediliyor}
                                color={secili === s.deger ? s.renk : "light"} className={secili === s.deger ? "" : "border"}
                                active={secili === s.deger} aria-pressed={secili === s.deger}
                                data-testid={`kkd-seviye-${k.kkdKalemiId}-${s.deger}`}
                                onClick={() => seviyeSec(k.kkdKalemiId, s.deger)}
                              >
                                {t(`kkdKurallari.seviyeler.${s.anahtar}`)}
                              </Button>
                            ))}
                          </ButtonGroup>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            </div>
          )}
        </CardBody>
        <div className="card-footer text-muted small">{genel ? t("kkdKurallari.aciklamaGenel") : t("kkdKurallari.aciklama")}</div>
      </Card>

      {degisenSayisi > 0 && (
        <Card className="position-sticky bottom-0 mb-0 shadow-lg" style={{ zIndex: 2 }} data-testid="kkd-kaydet-cubugu">
          <CardBody className="py-2 d-flex align-items-center justify-content-between gap-2">
            <span className="small text-muted">{t("kkdKurallari.kaydedilmemis", { n: degisenSayisi })}</span>
            <div className="d-flex gap-2">
              <Button color="light" size="sm" disabled={kaydediliyor} onClick={() => setTaslak(taslakOlustur(kural))}>
                {t("kkdKurallari.vazgec")}
              </Button>
              <Button color="primary" size="sm" disabled={kaydediliyor} onClick={kaydet} data-testid="kkd-kaydet">
                {kaydediliyor ? <Spinner size="sm" className="me-1" /> : <i className="bx bx-save me-1" />}{t("common:kaydet")}
              </Button>
            </div>
          </CardBody>
        </Card>
      )}
    </>
  );
}
